import { useQuery } from "@apollo/client/react";
import { useMemo } from "react";
import {
  Combobox,
  type ComboboxFieldProps,
  type ComboboxOption,
} from "@/common/components/ui/combobox";
import { Skeleton } from "@/common/components/ui/skeleton";
import {
  GetLedgerAccountsDocument,
  type GetLedgerAccountsQuery,
  type GetLedgerAccountsQueryVariables,
} from "@/graphql/definitions";
import { useTranslations } from "@/common/hooks/use-translations";
import { useSuggestionsFallback } from "./use-suggestions-fallback";

interface AccountComboboxProps extends ComboboxFieldProps {
  ledgerId: string;
  value: string;
  onValueChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  filterPrefix?: string;
}

/**
 * AccountCombobox component - searchable combobox for account selection
 * Uses GetLedgerAvailableAccounts GraphQL query to fetch non-closed accounts
 */
export function AccountCombobox({
  ledgerId,
  value,
  onValueChange,
  placeholder,
  disabled = false,
  className,
  filterPrefix,
  ...fieldProps
}: AccountComboboxProps) {
  const { t } = useTranslations();
  const defaultPlaceholder = placeholder || t("journal.selectAccount");

  const query = useQuery<
    GetLedgerAccountsQuery,
    GetLedgerAccountsQueryVariables
  >(GetLedgerAccountsDocument, {
    variables: { ledgerId, status: "open" },
    skip: !ledgerId,
    notifyOnNetworkStatusChange: true,
  });
  const { data } = query;

  // Convert accounts to ComboboxOption format, optionally filtering by prefix
  const options: ComboboxOption[] = useMemo(() => {
    if (!data?.getLedgerAccounts) return [];

    const accounts = filterPrefix
      ? data.getLedgerAccounts.filter((a) => a.startsWith(filterPrefix))
      : data.getLedgerAccounts;

    return accounts.map((account) => ({
      value: account,
      label: account,
      indent: account.split(":").length - 1, // Calculate indent based on colon separators
    }));
  }, [data, filterPrefix]);

  const fallback = useSuggestionsFallback(
    query,
    Boolean(data?.getLedgerAccounts),
    fieldProps["aria-describedby"],
  );

  if (fallback.initialLoading) {
    return <Skeleton className="h-10 w-full" />;
  }

  return (
    <Combobox
      options={fallback.failed ? [] : options}
      value={value}
      onValueChange={onValueChange}
      placeholder={defaultPlaceholder}
      allowCustom={true}
      emptyText={t("component.accountCombobox.noAccountsFound")}
      disabled={disabled}
      className={className}
      {...fieldProps}
      aria-describedby={fallback.describedBy}
      notice={fallback.notice}
    />
  );
}
